import WIPFeaturesContext from 'context/wip-features/wip-features.context';
import { useI18n } from 'hooks/useI18n';
import { UIOfferOffer } from 'interfaces/ui/ui-offer-offer';
import { UIOfferPerks } from 'interfaces/ui/ui-offer-perks';
import { useContext } from 'react';
import TooltipMF from '../../shared/TooltipMF';
import {
  StyledOfferSummary,
  StyledOfferSummaryBlock,
  StyledOfferSummaryItem,
  StyledOfferSummaryItemExtra,
  StyledOfferSummaryItemTitle,
  StyledOfferSummaryItemValue
} from './OfferSummary.styled';

const OfferSummary: React.FC<{
  offer: UIOfferOffer;
  perks: UIOfferPerks;
  migratedToMarkdown: boolean;
}> = ({ offer, perks, migratedToMarkdown }) => {
  const { t } = useI18n('job-offer');
  const showWIPFeatures = useContext(WIPFeaturesContext);

  const salaryMin = offer.salaryMin || null;
  const salaryMax = offer.salaryMax || null;
  const isFreelance = offer.isFreelance;

  return (
    <StyledOfferSummary>
      <StyledOfferSummaryBlock>
        <StyledOfferSummaryItem>
          <StyledOfferSummaryItemTitle>
            {isFreelance ? t('offer.fee') : t('offer.salary')}
          </StyledOfferSummaryItemTitle>

          {isFreelance ? (
            <StyledOfferSummaryItemValue as="span">
              <strong>
                {!!offer.hourlyRateTo && !offer.hourlyRateFrom && (
                  <span>{t('offer.up_to')} </span>
                )}
                {!!offer.hourlyRateFrom && !offer.hourlyRateTo && (
                  <span>{t('offer.from')} </span>
                )}
                {!!offer.hourlyRateFrom &&
                  offer.hourlyRateFrom !== offer.hourlyRateTo &&
                  offer.hourlyRateFrom}
                {!!offer.hourlyRateFrom &&
                  offer.hourlyRateFrom !== offer.hourlyRateTo &&
                  !!offer.hourlyRateTo && <span>-</span>}
                {!!offer.hourlyRateTo && offer.hourlyRateTo}
                {offer.currency}
              </strong>
              <span>/{t('common:salary.per_hour')}</span>
            </StyledOfferSummaryItemValue>
          ) : (
            <StyledOfferSummaryItemValue as="span">
              {!salaryMin && salaryMax && <span>{t('offer.up_to')} </span>}

              {salaryMin && !salaryMax && <span>{t('offer.from')} </span>}

              <span>{offer.currency}</span>

              {salaryMin && salaryMax && salaryMin !== salaryMax && (
                <>
                  <strong>{salaryMin}</strong>
                  <span>-</span>
                  <strong>{salaryMax}</strong>
                </>
              )}

              {salaryMin && salaryMax && salaryMin === salaryMax && (
                <>{salaryMax}</>
              )}

              {!salaryMin && salaryMax && <>{salaryMax}</>}

              {salaryMin && !salaryMax && <>{salaryMin}</>}

              <span>K</span>
            </StyledOfferSummaryItemValue>
          )}

          <StyledOfferSummaryItemExtra>
            {showWIPFeatures && perks.compensation?.length > 0 && (
              <> {t('offer.plusPerks')}</>
            )}
          </StyledOfferSummaryItemExtra>
        </StyledOfferSummaryItem>

        {offer.bonus && (
          <TooltipMF
            content={
              <span
                dangerouslySetInnerHTML={{ __html: t(offer.bonusInfo) || '' }}
              ></span>
            }
            direction="down"
          >
            <StyledOfferSummaryItem>
              <StyledOfferSummaryItemTitle>
                {t('offer.bonus')}
              </StyledOfferSummaryItemTitle>
              <StyledOfferSummaryItemValue as="span">
                <span className="extra">+</span>
                <span>{offer.currency}</span>
                {offer.bonus}
                <span>K</span>
              </StyledOfferSummaryItemValue>
            </StyledOfferSummaryItem>
          </TooltipMF>
        )}

        {(offer.equityInf !== null || offer.equitySup !== null) && (
          <StyledOfferSummaryItem>
            <StyledOfferSummaryItemTitle>
              {t('offer.equity')}
            </StyledOfferSummaryItemTitle>
            <StyledOfferSummaryItemValue as="span">
              {!offer.equityInf && offer.equitySup && (
                <span>{t('offer.up_to')} </span>
              )}
              {offer.equityInf && !offer.equitySup && (
                <span>{t('offer.from')} </span>
              )}
              {offer.equityInf &&
                offer.equitySup &&
                offer.equityInf !== offer.equitySup && (
                  <>
                    {offer.equityInf}
                    <span>%</span>
                    <span> - </span>
                    {offer.equitySup}
                    <span>%</span>
                  </>
                )}
              {offer.equityInf &&
                offer.equitySup &&
                offer.equityInf === offer.equitySup && (
                  <>
                    {offer.equitySup}
                    <span>%</span>
                  </>
                )}
              {!offer.equityInf && offer.equitySup && (
                <>
                  {offer.equitySup}
                  <span>%</span>
                </>
              )}
              {offer.equityInf && !offer.equitySup && (
                <>
                  {offer.equityInf}
                  <span>%</span>
                </>
              )}
            </StyledOfferSummaryItemValue>
          </StyledOfferSummaryItem>
        )}
      </StyledOfferSummaryBlock>

      {(offer.contract || offer.hours || offer.remote) && (
        <StyledOfferSummaryBlock>
          {!isFreelance && offer.contract && (
            <StyledOfferSummaryItem>
              <StyledOfferSummaryItemTitle>
                {t('offer.contract')}
              </StyledOfferSummaryItemTitle>
              <StyledOfferSummaryItemValue as="span">
                {offer.contract}
              </StyledOfferSummaryItemValue>
            </StyledOfferSummaryItem>
          )}

          {!isFreelance && offer.hours && (
            <StyledOfferSummaryItem>
              <StyledOfferSummaryItemTitle>
                {t('offer.schedule')}
              </StyledOfferSummaryItemTitle>
              <StyledOfferSummaryItemValue to="horario" href="#horario">
                {t('offer.flex')}
              </StyledOfferSummaryItemValue>
            </StyledOfferSummaryItem>
          )}

          {offer.remote && (
            <StyledOfferSummaryItem>
              <StyledOfferSummaryItemTitle>
                {t('offer.remote')}
              </StyledOfferSummaryItemTitle>
              <StyledOfferSummaryItemValue to="donde" href="#donde">
                {offer.remote}
              </StyledOfferSummaryItemValue>
            </StyledOfferSummaryItem>
          )}
        </StyledOfferSummaryBlock>
      )}
    </StyledOfferSummary>
  );
};

export default OfferSummary;
